import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ApiError } from "@home-health-notify/domain";
import {
  IOrgGeneralSettingsRequest,
  IOrgGeneralSettingsResponse,
} from "@home-health-notify/models";
import {
  ApiErrorHandler,
  useOrgGeneralSettings,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { timePickerViewRenderers } from "../common/ViewRenderers/timePickerViewRenderers";
import { Section, SettingsCard, TimeRow } from "./OrgGeneralSettings.style";
import {
  formatTime,
  fromPickerValue,
  IOrgSettingsForm,
  OrgSettingsErrors,
  serverErrorsToForm,
  toPickerValue,
  validateOrgSettings,
} from "./orgSettingsUtils";

const useOrgSettingsT = () => {
  const { t } = useTranslation();
  return (key: string, options?: object) =>
    t(key, { ns: "orgSettings", ...options });
};

interface IOrgGeneralSettingsFormProps {
  settings: IOrgGeneralSettingsResponse;
  isSaving: boolean;
  save: (request: IOrgGeneralSettingsRequest) => Promise<void>;
}

const OrgGeneralSettingsForm = ({
  settings,
  isSaving,
  save,
}: IOrgGeneralSettingsFormProps) => {
  const tNs = useOrgSettingsT();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<IOrgSettingsForm>({
    start: settings.workingHours.start,
    end: settings.workingHours.end,
    reminder: settings.preferredReminderTime,
  });
  const [serverErrors, setServerErrors] = useState<OrgSettingsErrors>({});

  const errors = { ...serverErrors, ...validateOrgSettings(form) };
  const hasErrors = Object.keys(errors).length > 0;
  const isDirty =
    form.start !== settings.workingHours.start ||
    form.end !== settings.workingHours.end ||
    form.reminder !== settings.preferredReminderTime;

  const setField = (field: keyof IOrgSettingsForm, value: string | null) => {
    setServerErrors({});
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const errorText = (field: keyof IOrgSettingsForm) => {
    const error = errors[field];
    if (!error) return undefined;
    return tNs(`errors.${error}`, {
      start: form.start ? formatTime(form.start) : "",
      end: form.end ? formatTime(form.end) : "",
    });
  };

  const onSave = async () => {
    if (!form.start || !form.end || !form.reminder) return;
    try {
      await save({
        preferredReminderTime: form.reminder,
        workingHours: { start: form.start, end: form.end },
      });
      enqueueSnackbar(tNs("saved"), { variant: "success" });
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar, undefined, {
        onApiError: (error: ApiError) => {
          const mapped = serverErrorsToForm(error.fieldErrors);
          if (Object.keys(mapped).length === 0) return false;
          setServerErrors(mapped);
          return true;
        },
      });
    }
  };

  const timeField = (
    field: keyof IOrgSettingsForm,
    label: string,
    helper?: string,
  ) => (
    <TimePicker
      label={label}
      value={toPickerValue(form[field])}
      onChange={value => setField(field, fromPickerValue(value))}
      viewRenderers={timePickerViewRenderers}
      disabled={isSaving}
      slotProps={{
        textField: {
          fullWidth: true,
          error: !!errors[field],
          helperText: errorText(field) ?? helper ?? " ",
        },
      }}
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <SettingsCard variant="outlined">
        <Section>
          <TextField
            fullWidth
            disabled
            label={tNs("timezone")}
            value={settings.timezone}
            helperText={tNs("timezoneHelper")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <LockOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
              htmlInput: { "data-testid": "org-settings-timezone" },
            }}
          />
        </Section>

        <Section>
          <Typography variant="subtitle2">{tNs("workingHours")}</Typography>
          <Typography variant="caption" color="text.secondary">
            {tNs("workingHoursHelper")}
          </Typography>
          <TimeRow>
            {timeField("start", tNs("start"))}
            {timeField("end", tNs("end"))}
          </TimeRow>
        </Section>

        <Section>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {tNs("reminderTime")}
          </Typography>
          {timeField(
            "reminder",
            tNs("reminderTime"),
            tNs("reminderTimeHelper"),
          )}
        </Section>

        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving || !isDirty || hasErrors}
          data-testid="org-settings-save"
        >
          {tNs("save")}
        </Button>
      </SettingsCard>
    </LocalizationProvider>
  );
};

export const OrgGeneralSettingsContent = () => {
  const tNs = useOrgSettingsT();
  const { settings, isLoading, isSaving, hasLoadError, reload, save } =
    useOrgGeneralSettings();

  return (
    <Box sx={{ p: 3, maxWidth: 620 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {tNs("title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tNs("subtitle")}
      </Typography>

      {hasLoadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              {tNs("retry")}
            </Button>
          }
        >
          {tNs("loadError")}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        settings && (
          // Remount on every server response so the form restarts from the
          // saved values (the backend may normalize them).
          <OrgGeneralSettingsForm
            key={JSON.stringify(settings)}
            settings={settings}
            isSaving={isSaving}
            save={save}
          />
        )
      )}
    </Box>
  );
};
